import { useEffect } from 'react';

import { useSubscriptionsDispatchContext } from '../SubscriptionsContextProvider';
import { type ConfluenceOnContentModified } from '../subscriptionsTypes';

/**
 * A hook to manage subscription events.
 * It registers a subscriber with the provided delta and calls the onDataReceived callback when data is received.
 * It also handles cleanup by removing the subscriber when the component unmounts or dependencies change
 * @param onDataReceived - Callback to be called when data is received for the delta
 * @param skip - If true, the subscription will not be registered
 * @param contentId - The content ID to subscribe to
 * @param contentType - The content type to subscribe to
 * @param delta - The delta to subscribe to
 * @param subscriberId - An ID for the subscriber, used to remove the subscriber later it will have a uuid appended to ensure uniqueness
 * @returns null
 */
export const useSubscriptionEvent = ({
	onDataReceived,
	skip = false,
	contentId,
	contentType,
	delta,
	subscriberId,
}: {
	onDataReceived:
		| ((eventName: string, response: ConfluenceOnContentModified) => void)
		| (() => void);
	skip?: boolean;
	contentId?: string | null;
	contentType?: string | null;
	delta: string;
	subscriberId: string;
}) => {
	const dispatch = useSubscriptionsDispatchContext();

	const { setContentId, setContentType, removeSubscriber, addSubscriber } = dispatch || {};

	useEffect(() => {
		if (skip || !addSubscriber || !contentId || !contentType || !removeSubscriber) {
			return;
		}
		setContentId?.(contentId);
		setContentType?.(contentType);
		const uniqueSubscriberId = addSubscriber({
			delta,
			subscriberId,
			onSubscriptionData: onDataReceived,
		});
		return () => {
			removeSubscriber({ delta, subscriberId: uniqueSubscriberId });
		};
	}, [
		skip,
		dispatch,
		contentId,
		contentType,
		setContentId,
		setContentType,
		addSubscriber,
		removeSubscriber,
		subscriberId,
		delta,
		onDataReceived,
	]);

	return null;
};
